import { BadgeCheck, Download, Leaf, Tag, User } from 'lucide-react';
import Link from 'next/link';
import i18n from '@/settings/i18n';
import settings, { priceFormater } from '@/settings/index';

const t = i18n.init('business');

const BUSINESS_EMAIL = 'business@reebelo.com';

// Minimum order value that unlocks automatic volume discounts.
const VOLUME_DISCOUNT_THRESHOLD = 2000;

const EXEMPTION_CERTIFICATE_HREF = '/business/tax-exempt';

const ICON_CLASS = 'h-5 w-5 text-[#E9F8F9]';

const taxLabel = () => {
  if (['reebelo-au', 'reebelo-nz', 'quista'].includes(settings.store)) {
    return t`GST Invoice`;
  }

  if (
    [
      'reebelo-uk',
      'reebelo-fr',
      'reebelo-de',
      'reebelo-es',
      'reebelo-it',
      'reebelo-se',
      'reebelo-ie',
      'reebelo-nl',
      'reebelo-at',
      'reebelo-be',
      'reebelo-pt',
      'reebelo-fi',
    ].includes(settings.store)
  ) {
    return t`VAT Invoice`;
  }

  if (['reebelo-us'].includes(settings.store)) {
    return t`Sales Tax Exemption`;
  }

  return t`Tax Invoice`;
};

const taxContent = () => {
  if (['reebelo-au', 'reebelo-nz', 'quista'].includes(settings.store)) {
    return t`GST Invoice provided for your records`;
  }

  if (
    [
      'reebelo-uk',
      'reebelo-fr',
      'reebelo-de',
      'reebelo-es',
      'reebelo-it',
      'reebelo-se',
      'reebelo-ie',
      'reebelo-nl',
      'reebelo-at',
      'reebelo-be',
      'reebelo-pt',
      'reebelo-fi',
    ].includes(settings.store)
  ) {
    return t`VAT Invoice provided for your records`;
  }

  if (['reebelo-us'].includes(settings.store)) {
    return (
      <>
        <Link href={EXEMPTION_CERTIFICATE_HREF} className="underline">
          {t`Submit your exemption certificate`}
        </Link>{' '}
        {t`and buy sales tax-free`}
      </>
    );
  }

  return t`Tax Invoice provided for your records`;
};

const Why = () => {
  const features = [
    {
      icon: <BadgeCheck className={ICON_CLASS} strokeWidth={2} />,
      title: t`Verified Quality`,
      description: t`12 Months warranty & 30 Day risk-free trial`,
    },
    {
      icon: <Tag className={ICON_CLASS} strokeWidth={2} />,
      title: t`Volume Discounts`,
      description: t`Automatically applied at checkout for orders above ${{
        amount: priceFormater.short(VOLUME_DISCOUNT_THRESHOLD, false),
      }}`,
    },
    {
      icon: <Download className={ICON_CLASS} strokeWidth={2} />,
      title: taxLabel(),
      description: taxContent(),
    },
    {
      icon: <User className={ICON_CLASS} strokeWidth={2} />,
      title: t`Dedicated Support`,
      description: (
        <>
          {t`High-priority customer support under`}{' '}
          <Link href={`mailto:${BUSINESS_EMAIL}`} className="underline">
            {BUSINESS_EMAIL}
          </Link>
        </>
      ),
    },
    {
      icon: <Leaf className={ICON_CLASS} strokeWidth={2} />,
      title: t`Positive ESG Impact`,
      description: t`Up to 90% less CO2 & rare earth materials than new`,
    },
  ];

  return (
    <section className="bg-[#E9F8F9] py-12 text-gray-700 md:py-16">
      <div className="reebelo-container">
        <h2 className="text-xl font-bold xs:text-center md:text-2xl">
          {t`Why businesses choose Reebelo`}
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-8 xxs:grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 xl:gap-4">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="flex flex-row items-center gap-4 xs:flex-col xs:text-center"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-700 xs:h-12 xs:w-12">
                {feature.icon}
              </div>
              <div>
                <h3 className="text-base font-medium xs:text-lg">
                  {feature.title}
                </h3>
                <p className="max-w-[220px] text-sm leading-snug text-gray-700/70">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Why;
